import React, { lazy, Suspense } from 'react';
import cn from 'classnames';

import LazyImage from 'components/UI/LazyImage';
import MediaPlayer from 'components/MediaPlayer';
import PersonalData from './PersonalData';
import ReviewPlatform from './ReviewPlatform';
import { useTrans } from 'utils/localisation';
import { useStateModal } from 'utils/hooks';

import * as styles from './ReviewInfo.module.scss';

const TestimonialsModal = lazy(() => import('components/Modals/TestimonialsModal'));

export default function ReviewInfo({
  budget,
  case_url,
  verified,
  company,
  company_link,
  company_logo,
  country,
  country_icon,
  description,
  domain,
  duration,
  linkedin,
  name,
  photo,
  position,
  short_description,
  summary,
  team_size,
  technology,
  theme,
  video,
  setVideoPlayback,
  setPopupOpen,
}) {
  const t = useTrans(['testimonials', 'common']);
  const { modalState, showModal, hideModal } = useStateModal();
  const modalData = {
    summary,
    domain,
    team_size,
    duration,
    budget,
    technology,
    country,
    country_icon,
    case_url,
  };

  const showPopup = () => {
    showModal();
    if (typeof setPopupOpen === 'function') {
      setPopupOpen(true);
    }
  };

  const hidePopup = () => {
    hideModal();
    if (typeof setPopupOpen === 'function') {
      setPopupOpen(false);
    }
  };

  return (
    <article className={cn(styles.card, theme && styles[theme])}>
      <div className={styles.wrapper}>
        <section className={styles.section}>
          {video ? (
            <>
              <p className={cn(styles.shortDescription)}>{short_description}</p>
              <MediaPlayer
                preload="metadata"
                className={styles.mediaPlayer}
                videoPoster={video.poster}
                videoSrc={video.url}
                videoCaption={{
                  title: name,
                  linkedin,
                  position,
                  company,
                  companyLink: company_link,
                }}
                theme="testimonial"
                setGlobalPlayback={setVideoPlayback}
              />
            </>
          ) : (
            <>
              {verified && <ReviewPlatform className={styles.reviewPlatform} {...verified} />}
              <p className={styles.shortDescription}>{short_description}</p>
              <p className={styles.review}>{description}</p>
              <PersonalData
                photo={photo}
                name={name}
                linkedin={linkedin}
                position={position}
                company={company}
                companyLink={company_link}
              />
            </>
          )}
          <div className={cn(styles.popupLink, styles.hideTablet)} onClick={showPopup}>
            {t('readMorePopup')}
          </div>
        </section>
        <section className={cn(styles.section, styles.hideMobile)}>
          {company_logo && (
            <div className={styles.logoWrapper}>
              <LazyImage className={styles.logo} src={company_logo} alt="Company logo" />
            </div>
          )}
          <p className={styles.title}>{t('projectSummary')}</p>
          <p className={styles.text}>{summary}</p>
          {country_icon && (
            <>
              <p className={styles.title}>{t('country')}</p>
              <figure className={styles.location}>
                <LazyImage
                  className={styles.locationImg}
                  src={country_icon}
                  alt={`${country} flag`}
                />
                <figcaption>{country}</figcaption>
              </figure>
            </>
          )}
          <div className={styles.popupLink} onClick={showPopup}>
            {t('readMorePopup')}
          </div>
        </section>
      </div>
      <Suspense fallback={null}>
        <TestimonialsModal
          isOpen={modalState.isOpen}
          closeModalCallback={hidePopup}
          {...modalData}
        />
      </Suspense>
    </article>
  );
}
